import React, { useEffect, useState } from 'react';
import { Heart, Phone } from 'lucide-react';
import apiService from '../services/apiService';

const DAYS = 30;

const formatDate = (isoDate) =>
    new Date(`${isoDate}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

const relativeDay = (daysUntil) => {
    if (daysUntil === 0) return "Aujourd'hui";
    if (daysUntil === 1) return 'Demain';
    return `Dans ${daysUntil} jours`;
};

/**
 * Anniversaires de mariage à venir.
 * Le backend filtre selon le rôle : sa zone pour le comité de zone, toutes les zones pour le noyau diocésain.
 *
 * @param {boolean} showZone     Affiche la zone de chaque couple (dashboard diocésain)
 * @param {string}  cardClassName Classes du conteneur, pour s'aligner sur le style du dashboard hôte
 */
const WeddingAnniversariesWidget = ({ showZone = false, cardClassName = 'bg-white rounded-lg shadow-md overflow-hidden' }) => {
    const [anniversaries, setAnniversaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        apiService.get(`/zone-dashboard/anniversaries?days=${DAYS}`)
            .then((response) => {
                if (!cancelled) setAnniversaries(response?.anniversaries || []);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    return (
        <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">
                Anniversaires de mariage
                <span className="ml-2 text-sm font-normal text-gray-500">{DAYS} prochains jours</span>
            </h2>

            <div className={cardClassName}>
                {loading && (
                    <p className="p-4 sm:p-6 text-sm text-gray-500">Chargement...</p>
                )}

                {!loading && error && (
                    <p className="p-4 sm:p-6 text-sm text-gray-500">Impossible de charger les anniversaires.</p>
                )}

                {!loading && !error && anniversaries.length === 0 && (
                    <div className="text-center py-6 sm:py-8 text-gray-500">
                        <Heart className="h-10 w-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm sm:text-base">Aucun anniversaire de mariage dans les {DAYS} prochains jours</p>
                    </div>
                )}

                {!loading && !error && anniversaries.length > 0 && (
                    <ul className="divide-y divide-gray-200">
                        {anniversaries.map((couple) => (
                            <li
                                key={`${couple.name}-${couple.date}`}
                                className={`p-4 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${couple.days_until <= 1 ? 'bg-rose-50' : ''}`}
                            >
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="p-2 bg-rose-100 rounded-full flex-shrink-0">
                                        <Heart className="h-4 w-4 text-rose-600" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{couple.name}</p>
                                        <p className="text-xs sm:text-sm text-gray-600">
                                            {couple.years} {couple.years > 1 ? 'ans' : 'an'} de mariage
                                            {showZone && couple.zone ? ` · Zone ${couple.zone}` : ''}
                                            {couple.fraternity ? ` · ${couple.fraternity}` : ''}
                                        </p>
                                        {couple.phones?.length > 0 && (
                                            <p className="text-xs sm:text-sm text-gray-500 flex flex-wrap items-center gap-x-3 mt-0.5">
                                                <Phone className="h-3 w-3" />
                                                {couple.phones.map((phone) => (
                                                    <a key={phone} href={`tel:${phone}`} className="hover:text-gray-800">{phone}</a>
                                                ))}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="sm:text-right flex-shrink-0 pl-11 sm:pl-0">
                                    <p className={`text-sm font-medium ${couple.days_until <= 1 ? 'text-rose-700' : 'text-gray-800'}`}>
                                        {relativeDay(couple.days_until)}
                                    </p>
                                    <p className="text-xs text-gray-500 capitalize">{formatDate(couple.date)}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default WeddingAnniversariesWidget;
